import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import submit from "submit-form";
import { round } from "lodash";

import Modal from "../modal-new";
import DateInput from "../inputs/date-input";
import ModalFooter from "./ModalFooter";
import NutrientsInputs from "../../views/athlete-portal/nutrition/components/NutrientsInputs";
import KcalInputs from "../../views/athlete-portal/nutrition/components/KcalInputs";
import { validateNutritionPlan } from "../../utils/nutrition-validations";
import NutrientsChart from "../../views/athlete-portal/nutrition/components/NutrientsChart";
import { defaultFirebaseProjectId } from "../../constants";
import RadioInput from "../radio-input";
import SvgIcon from "../SvgIcon";

const getInitialStartDate = (editPlan, isCopy, copyPlanDates) => {
  const result = isCopy
    ? moment(String(copyPlanDates.startDate)).toDate()
    : moment(String(editPlan.utcStartDate)).toDate();

  return result;
};

const getInitialEndDate = (editPlan, isCopy, copyPlanDates) => {
  const result = isCopy
    ? moment(String(copyPlanDates.endDate)).toDate()
    : moment(String(editPlan.utcEndDate)).toDate();

  return result;
};

class CreatePlanModal extends React.Component {
  constructor(props) {
    super(props);

    const { editPlan, copy, copyPlanDates } = props;

    const initialStartDate = editPlan
      ? getInitialStartDate(editPlan, copy, copyPlanDates)
      : moment().toDate();

    const initialEndDate = editPlan
      ? getInitialEndDate(editPlan, copy, copyPlanDates)
      : moment().add(15, "days").toDate();

    this.initialFormValues = {
      startDate: initialStartDate,
      endDate: initialEndDate,
      startingVar: "kcal",
      proteins: editPlan ? editPlan.proteins : "",
      carbs: editPlan ? editPlan.carbs : "",
      fats: editPlan ? editPlan.fats : "",
      fibers: editPlan ? editPlan.fibers : "",
      calories: editPlan ? editPlan.calories : "",
      proteinsPercent: editPlan
        ? round(((editPlan.proteins * 4) / editPlan.calories) * 100, 1)
        : "34",
      carbsPercent: editPlan
        ? round(((editPlan.carbs * 4) / editPlan.calories) * 100, 1)
        : "33",
      fatsPercent: editPlan
        ? round(((editPlan.fats * 9) / editPlan.calories) * 100, 1)
        : "33",
      restDayActive: editPlan ? !!editPlan.restDay : false,
      restDayProteins:
        editPlan && editPlan.restDay ? editPlan.restDay?.proteins : "",
      restDayCarbs: editPlan && editPlan.restDay ? editPlan.restDay?.carbs : "",
      restDayFats: editPlan && editPlan.restDay ? editPlan.restDay?.fats : "",
      restDayFibers:
        editPlan && editPlan.restDay ? editPlan.restDay?.fibers : "",
      restDayCalories:
        editPlan && editPlan.restDay ? editPlan.restDay?.calories : "",
      restDayProteinsPercent:
        editPlan && editPlan.restDay
          ? round(
              ((editPlan.restDay?.proteins * 4) / editPlan.restDay?.calories) *
                100,
              1
            )
          : "34",
      restDayCarbsPercent:
        editPlan && editPlan.restDay
          ? round(
              ((editPlan.restDay?.carbs * 4) / editPlan.restDay?.calories) *
                100,
              1
            )
          : "33",
      restDayFatsPercent:
        editPlan && editPlan.restDay
          ? round(
              ((editPlan.restDay?.fats * 9) / editPlan.restDay?.calories) * 100,
              1
            )
          : "33",
      notes: editPlan && editPlan.notes ? editPlan.notes : "",
    };

    this.state = {
      form: Object.assign({}, this.initialFormValues),
      submitted: false,
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleDateChange = this.handleDateChange.bind(this);
    this.handleRestDayActive = this.handleRestDayActive.bind(this);
  }

  handleChange(e, updates = null) {
    e.persist();
    console.log("changing==", e);
    const { name, value } = e.target;
    console.log(name, value);
    const { form } = this.state;

    if (name === "startingVar") {
      this.setState({
        form: Object.assign(
          {},
          {
            ...this.initialFormValues,
            startDate: form.startDate,
            endDate: form.endDate,
          },
          { [name]: value }
        ),
      });
      return;
    }

    const formCopy = Object.assign(
      {},
      form,
      updates ? updates : { [name]: value }
    );

    this.setState({ form: formCopy });
  }

  handleRestDayActive(e) {
    const { form } = this.state;

    const formCopy = Object.assign({}, form);
    formCopy.restDayActive = !form.restDayActive;

    this.setState({ form: formCopy });
  }

  handleNoteChange(value) {
    const { form } = this.state;

    const formCopy = Object.assign({}, form);
    formCopy.notes = value;

    this.setState({ form: formCopy });
  }

  handleDateChange(date, field) {
    const { form } = this.state;
    const formCopy = Object.assign({}, form);
    formCopy[field] = date;

    this.setState({ form: formCopy });
  }

  handleSubmit(e) {
    e.preventDefault();

    const { locals } = this.context;
    const { assert, flash, query, nutritionPlans } = locals;
    const { form, submitted } = this.state;
    const { editPlan, copy } = this.props;

    if (submitted) return;

    this.setState({ submitted: true });

    const formCopy = Object.assign({}, form);

    const coachAvatar =
      !!locals.activeUser.avatar && !!locals.activeUser.avatar.file
        ? locals.activeUser.avatar.file
        : null;

    const firebaseProjectId = !!locals.activeUserToRequest.firebaseProjectId
      ? locals.activeUserToRequest.firebaseProjectId
      : defaultFirebaseProjectId;

    const body = validateNutritionPlan(
      formCopy,
      editPlan,
      coachAvatar,
      firebaseProjectId,
      assert,
      flash,
      nutritionPlans
    );

    if (body) {
      submit(
        `/actions/nutrition/save/${
          editPlan && !copy ? editPlan._id : ""
        }?proxyAthlete=${query.athlete}`,
        {
          method: "POST",
          body,
        }
      );
    } else {
      this.setState({ submitted: false });
    }
  }

  handleReset(e) {
    e.preventDefault();
    this.setState({ form: this.initialFormValues });
  }

  renderLastPlanInfo(plan) {
    const { calories, carbs, fats, proteins, utcStartDate, utcEndDate } = plan;
    return (
      <div className="chip info">
        <span>Previous plan</span>
        <span>
          {moment(String(utcStartDate)).format("MMM D, YYYY")} -{" "}
          {moment(String(utcEndDate)).format("MMM D, YYYY")} | kCal {calories} |
          P {proteins}g | C {carbs}g | F {fats}g
        </span>
      </div>
    );
  }

  render() {
    const { form } = this.state;
    const { editPlan, copy } = this.props;
    const { locals } = this.context;
    const mealPlan = editPlan?.mealPlan || {};
    const hasMealPlan = mealPlan && Object.keys(mealPlan).length > 0;
    const {
      startDate,
      endDate,
      startingVar,
      calories,
      proteins,
      carbs,
      fats,
      fibers,
      proteinsPercent,
      carbsPercent,
      fatsPercent,
      restDayActive,
      restDayProteins,
      restDayCarbs,
      restDayFats,
      restDayFibers,
      restDayCalories,
      restDayProteinsPercent,
      restDayCarbsPercent,
      restDayFatsPercent,
      notes,
    } = form;

    const nutrientsMode = startingVar === "nutrients";

    const lastPlan = (locals.nutritionPlans || [])[0];
    const showLastPlan = !!lastPlan && !editPlan;

    return (
      <form onSubmit={(e) => this.handleSubmit(e)} id="create-nutrition-plan">
        <Modal
          toClear={["plan", "copy"]}
          title={
            editPlan && !copy ? "Edit Nutrition Plan" : "Create a New Plan"
          }
        >
          <div className="body Create-Nutrition-Plan">
            {showLastPlan && this.renderLastPlanInfo(lastPlan)}
            <div className="form-row" role="calendar-date-selectors">
              <div className="form-col">
                <label className="label req" htmlFor="startDate">
                  Start date
                </label>
                <DateInput
                  name="startDate"
                  placeholder="Start Date"
                  value={startDate}
                  max={endDate}
                  onChange={(date) => this.handleDateChange(date, "startDate")}
                  isDisabled={hasMealPlan && !copy}
                />
              </div>

              <div className="form-col">
                <label className="label req" htmlFor="endDate">
                  End date
                </label>
                <DateInput
                  name="endDate"
                  placeholder="End Date"
                  value={endDate}
                  min={startDate}
                  onChange={(date) => this.handleDateChange(date, "endDate")}
                  isDisabled={hasMealPlan && !copy}
                />
              </div>
            </div>

            {hasMealPlan && !copy && (
              <div className="info-published">
                <SvgIcon
                  src={`/img/new-icons/danger-circle.svg`}
                  alt="icon"
                  noFilterOnLight={true}
                />
                <span>
                  Cannot edit the date of a nutrition plan when a meal plan has
                  already been added
                </span>
              </div>
            )}

            <div className="nutrition-plan-notes-row">
              <div className="form-col w-1/4">
                <label>Starting variable</label>
                <div className="list-wrapper">
                  <label for="startingVarKcal" className="label">
                    <input
                      name="startingVar"
                      id="startingVarKcal"
                      type="radio"
                      value="kcal"
                      className="radio"
                      onChange={this.handleChange}
                      checked={startingVar === "kcal" ? "checked" : undefined}
                    />
                    kCal Target
                  </label>
                  <label for="startingVarNutrients" className="label">
                    <input
                      name="startingVar"
                      id="startingVarNutrients"
                      type="radio"
                      value="nutrients"
                      className="radio"
                      onChange={this.handleChange}
                      checked={startingVar === "nutrients"}
                    />
                    Macronutrients
                  </label>
                </div>
              </div>

              <div className="w-3/4">
                <div className="form-col">
                  <label>Nutrition plan notes</label>
                  <textarea
                    value={notes || ""}
                    onChange={({ target: { value } }) =>
                      this.handleNoteChange(value)
                    }
                    id="coach-notes-textarea"
                    placeholder="Enter note here"
                  />
                </div>
              </div>
            </div>

            <div className="nutrition-plan-macros-container">
              <div className="nutrition-plan-day-title">
                <p>Training Day</p>
              </div>
              <MacrosInputs
                nutrientsMode={nutrientsMode}
                calories={calories}
                proteins={proteins}
                carbs={carbs}
                fats={fats}
                fibers={fibers}
                proteinsPercent={proteinsPercent}
                carbsPercent={carbsPercent}
                fatsPercent={fatsPercent}
                isRestDay={false}
                nutrientChartId={"nutrient-chart-create-plan-training-day"}
                handleChange={this.handleChange}
              />

              <div
                className={
                  restDayActive
                    ? "nutrition-plan-rest-day-active"
                    : "nutrition-plan-rest-day"
                }
              >
                <span className="flex flex-row items-center gap-2">
                  <SvgIcon
                    src={
                      restDayActive
                        ? "/img/new-icons/checked-square.svg"
                        : "/img/new-icons/unchecked-square.svg"
                    }
                    onClickFn={this.handleRestDayActive}
                    noFilterOnLight={!!restDayActive}
                  />
                  <p>Rest Day</p> <p>(Optional)</p>
                </span>
              </div>

              {restDayActive && (
                <MacrosInputs
                  nutrientsMode={nutrientsMode}
                  calories={restDayCalories}
                  proteins={restDayProteins}
                  carbs={restDayCarbs}
                  fats={restDayFats}
                  fibers={restDayFibers}
                  proteinsPercent={restDayProteinsPercent}
                  carbsPercent={restDayCarbsPercent}
                  fatsPercent={restDayFatsPercent}
                  isRestDay={true}
                  nutrientChartId={"nutrient-chart-create-plan-rest-day"}
                  handleChange={this.handleChange}
                />
              )}
            </div>
          </div>

          <ModalFooter
            onClickButton={(e) => this.handleSubmit(e)}
            onClickAltButton={(e) => this.handleReset(e)}
            altButtonText="Reset"
            classNameButton={`button ${true ? "save" : "alt-disabled"}`}
          />
        </Modal>
      </form>
    );
  }
}

const MacrosInputs = (props) => {
  const {
    nutrientsMode,
    calories,
    proteins,
    carbs,
    fats,
    fibers,
    proteinsPercent,
    carbsPercent,
    fatsPercent,
    isRestDay,
    nutrientChartId,
    handleChange,
  } = props;

  return (
    <div className="flex flex-row justify-between items-center mt-4 p-4">
      <div className="w-[50%]">
        {nutrientsMode ? (
          <NutrientsInputs
            handleChange={handleChange}
            proteins={proteins}
            carbs={carbs}
            fats={fats}
            isRestDay={isRestDay}
          />
        ) : (
          <KcalInputs
            calories={calories}
            proteins={proteins}
            proteinsPercent={proteinsPercent}
            carbs={carbs}
            carbsPercent={carbsPercent}
            fats={fats}
            fibers={fibers}
            fatsPercent={fatsPercent}
            handleChange={handleChange}
            isRestDay={isRestDay}
          />
        )}
      </div>

      <div className="w-[50%]">
        <NutrientsChart
          key={nutrientChartId}
          providedId={nutrientChartId}
          dayStats={{ nutrients: { proteins, carbs, fats } }}
          width={250}
        />
      </div>
    </div>
  );
};

CreatePlanModal.contextTypes = { locals: PropTypes.object };
export default CreatePlanModal;
